import { mileageDeductionFor } from "./taxData.js";
// Pure helpers for the business tools: invoices, recurring invoices, mileage,
// staff hours and profit & loss. No React in here so it's easy to test.

export const pad2 = (n) => String(n).padStart(2, "0");
export const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const localToday = () => isoOf(new Date());
const parse = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };

export function addDays(iso, n) { const d = parse(iso); d.setDate(d.getDate() + n); return isoOf(d); }

// Month-aware: Jan 31 + 1 month = Feb 28/29, and the next one goes back to the
// 31st instead of drifting to the 28th forever (anchorDay holds the original).
export function addMonths(iso, n, anchorDay) {
  const d = parse(iso);
  const day = anchorDay || d.getDate();
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  first.setDate(Math.min(day, last));
  return isoOf(first);
}

export const FREQUENCIES = [
  { id: "weekly", label: "Weekly" },
  { id: "biweekly", label: "Every 2 weeks" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "yearly", label: "Yearly" },
];

export function advance(iso, frequency, anchorDay) {
  switch (frequency) {
    case "weekly": return addDays(iso, 7);
    case "biweekly": return addDays(iso, 14);
    case "quarterly": return addMonths(iso, 3, anchorDay);
    case "yearly": return addMonths(iso, 12, anchorDay);
    default: return addMonths(iso, 1, anchorDay);
  }
}

// ---- invoices --------------------------------------------------------------

export function invoiceTotals(inv) {
  const subtotal = (inv.items || []).reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const rate = Number(inv.taxRate) || 0;
  const tax = Math.round(subtotal * rate) / 100;
  return { subtotal, taxRate: rate, tax, total: subtotal + tax };
}

// ---- partial payments ----------------------------------------------------------
// An invoice can be paid in pieces: inv.payments = [{ id, date, amount, method }].
// Older invoices that were simply marked paid have no payments list; they count as paid in full.
const cents = (n) => Math.round((Number(n) || 0) * 100) / 100;
export function invoicePaidAmount(inv) {
  if (Array.isArray(inv.payments) && inv.payments.length) return cents(inv.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0));
  return inv.status === "paid" ? cents(invoiceTotals(inv).total) : 0;
}
export function invoiceBalance(inv) {
  return Math.max(0, cents(invoiceTotals(inv).total - invoicePaidAmount(inv)));
}
export function isPartiallyPaid(inv) {
  return inv.status !== "paid" && inv.status !== "draft" && invoicePaidAmount(inv) > 0 && invoiceBalance(inv) > 0;
}

// Adds a payment and keeps the status in step: a payment that clears the balance marks the invoice paid.
export function applyPayment(inv, payment) {
  const pay = { id: payment.id, date: payment.date, amount: cents(payment.amount), method: String(payment.method || "").trim() };
  const payments = [...(Array.isArray(inv.payments) ? inv.payments : []), pay].sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));
  const next = { ...inv, payments };
  if (invoiceBalance(next) <= 0) {
    next.status = "paid";
    next.paidDate = payments[payments.length - 1].date;
    next.paidMethod = payments[payments.length - 1].method || inv.paidMethod || "";
  } else {
    if (next.status === "draft" || next.status === "paid") next.status = "sent";
    delete next.paidDate;
  }
  return next;
}

export function removePayment(inv, paymentId) {
  const payments = (inv.payments || []).filter((p) => p.id !== paymentId);
  const next = { ...inv, payments };
  if (next.status === "paid" && invoiceBalance(next) > 0) { next.status = "sent"; delete next.paidDate; }
  if (payments.length === 0) delete next.paidMethod;
  return next;
}

export function nextInvoiceNumber(invoices, prefix = "INV-") {
  let max = 0;
  (invoices || []).forEach((i) => { const m = String(i.number || "").match(/(\d+)\s*$/); if (m) max = Math.max(max, parseInt(m[1], 10)); });
  return `${prefix}${String(max + 1).padStart(4, "0")}`;
}

// Turns any recurring-invoice templates that are due into real (draft) invoices,
// catching up on every missed period. Returns the same profile object if
// nothing was due, so callers can skip a state update.
export function generateRecurringInvoices(profile, today = localToday()) {
  const templates = profile.recurringInvoices || [];
  if (!templates.length) return { profile, count: 0 };
  let invoices = [...(profile.invoices || [])];
  let count = 0;
  const prefix = profile.business?.invoicePrefix || "INV-";
  const updated = templates.map((t) => {
    if (t.paused) return t;
    let next = t.nextDate, guard = 0;
    while (next && next <= today && guard < 24) {
      invoices = [{
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
        clientId: t.clientId || null,
        number: nextInvoiceNumber(invoices, prefix),
        issueDate: next,
        dueDate: addDays(next, t.dueDays ?? 14),
        notes: t.notes || "",
        billTo: t.billTo || "",
        taxRate: t.taxRate || 0,
        items: (t.items || []).map((it) => ({ ...it, id: Math.random().toString(36).slice(2, 10) })),
        status: t.autoSent ? "sent" : "draft",
        fromRecurring: t.id,
      }, ...invoices];
      count++; guard++;
      next = advance(next, t.frequency, t.anchorDay);
    }
    return { ...t, nextDate: next };
  });
  if (count === 0) return { profile, count: 0 };
  return { profile: { ...profile, invoices, recurringInvoices: updated }, count };
}

// ---- periods & P&L ---------------------------------------------------------

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// kind: "month" | "quarter" | "year"; offset: 0 = current, -1 = previous, …
export function periodRange(kind, offset = 0, now = new Date()) {
  const y = now.getFullYear(), m = now.getMonth();
  if (kind === "year") {
    const yr = y + offset;
    return { start: `${yr}-01-01`, end: `${yr}-12-31`, label: String(yr), short: String(yr) };
  }
  if (kind === "quarter") {
    const idx = Math.floor(m / 3) + offset;
    const yr = y + Math.floor(idx / 4), q = ((idx % 4) + 4) % 4;
    const sm = q * 3;
    const endDay = new Date(yr, sm + 3, 0).getDate();
    return { start: `${yr}-${pad2(sm + 1)}-01`, end: `${yr}-${pad2(sm + 3)}-${pad2(endDay)}`, label: `Q${q + 1} ${yr}`, short: `Q${q + 1}` };
  }
  const d = new Date(y, m + offset, 1);
  const endDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return {
    start: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`,
    end: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(endDay)}`,
    label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, short: MONTHS[d.getMonth()].slice(0, 3),
  };
}

const inRange = (date, r) => date >= r.start && date <= r.end;

export function mileageDeduction(trips, rate) {
  return (trips || []).reduce((s, t) => s + (Number(t.distance) || 0), 0) * (Number(rate) || 0);
}

export function laborCost(hours, staff) {
  const rate = new Map((staff || []).map((s) => [s.id, Number(s.payRate) || 0]));
  return (hours || []).reduce((s, h) => s + (Number(h.hours) || 0) * (rate.get(h.staffId) || 0), 0);
}

// Profit & loss for one period.
// Revenue is cash-basis: an invoice counts on the day it was marked paid, and
// sales tax collected is left out (it isn't yours).
export function computePL(profile, range) {
  const biz = profile.business || {};
  // Partial payments: each payment counts on its own date, with the sales-tax share left out.
  let invoiceRevenue = 0;
  const paidInvoices = [];
  (profile.invoices || []).forEach((i) => {
    const t = invoiceTotals(i);
    if (Array.isArray(i.payments) && i.payments.length) {
      const share = t.total > 0 ? t.subtotal / t.total : 1;
      const inPeriod = i.payments.filter((p) => inRange(p.date, range));
      if (inPeriod.length) { paidInvoices.push(i); invoiceRevenue += inPeriod.reduce((s, p) => s + (Number(p.amount) || 0), 0) * share; }
    } else if (i.status === "paid" && inRange(i.paidDate || i.issueDate, range)) {
      paidInvoices.push(i); invoiceRevenue += t.subtotal;
    }
  });
  const otherIncome = (profile.income || []).filter((i) => inRange(i.date, range));
  const otherRevenue = otherIncome.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const allExp = (profile.expenses || []).filter((e) => inRange(e.date, range));
  const counted = allExp.filter((e) => e.deductible !== false);
  const byCategory = {};
  counted.forEach((e) => { byCategory[e.category] = (byCategory[e.category] || 0) + e.amount; });
  const categoryList = Object.entries(byCategory).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total);
  const expenseTotal = counted.reduce((s, e) => s + e.amount, 0);

  const trips = (profile.mileage || []).filter((t) => inRange(t.date, range));
  const mileage = biz.plIncludeMileage === false ? 0 : mileageDeductionFor(profile.mileage || [], biz, profile.currency, profile.country, range);

  // Payroll: wages come from payslips (what was actually paid), plus employer-side costs. Hours already on a payslip are
  // never counted again; hours not yet on a payslip can optionally be estimated at their pay rate.
  const slips = (profile.payslips || []).filter((p) => inRange(p.issuedDate, range));
  const wages = biz.plIncludePayslips === false ? 0 : slips.reduce((s, p) => s + (p.calc?.gross || 0), 0);
  const employerCosts = biz.plIncludePayslips === false ? 0 : slips.reduce((s, p) => s + (p.calc?.employerCost || 0), 0);
  const hrs = (profile.hours || []).filter((h) => inRange(h.date, range) && !h.payslipId);
  const unpaidLabor = biz.plIncludeLabor ? laborCost(hrs, profile.staff) : 0;
  const labor = wages + employerCosts + unpaidLabor;

  const revenue = invoiceRevenue + otherRevenue;
  const expenses = expenseTotal + mileage + labor;
  return {
    revenue, invoiceRevenue, otherRevenue, paidInvoiceCount: paidInvoices.length, otherIncome,
    expenseTotal, categoryList, mileage, labor, wages, employerCosts, unpaidLabor, payslipCount: slips.length, expenses,
    excludedCount: allExp.length - counted.length,
    profit: revenue - expenses,
    margin: revenue > 0 ? ((revenue - expenses) / revenue) * 100 : null,
  };
}

export function plSeries(profile, kind, endOffset = 0, n = 6) {
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const r = periodRange(kind, endOffset - i);
    const pl = computePL(profile, r);
    out.push({ label: r.short, revenue: pl.revenue, expenses: pl.expenses, profit: pl.profit });
  }
  return out;
}

// ---- hours -----------------------------------------------------------------

export function hoursFromTimes(start, end, breakMin = 0) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number), [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60; // overnight shift
  mins -= Number(breakMin) || 0;
  return mins > 0 ? Math.round((mins / 60) * 100) / 100 : null;
}

export function weekStart(iso) {
  const d = parse(iso);
  const diff = d.getDay() === 0 ? -6 : 1 - d.getDay();
  d.setDate(d.getDate() + diff);
  return isoOf(d);
}

// ---- misc ------------------------------------------------------------------

export function toCSV(rows) {
  return rows.map((r) => r.map((c) => {
    const s = String(c ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
}

export function downloadText(filename, text, type = "text/csv") {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Shrinks a logo to something that's cheap to store and sync (≈20–60 KB).
// A logo with a transparent background is placed on white, so it never turns black
// (JPEG has no transparency, and some PDF viewers draw transparent PNGs on black).
export function fileToLogoDataURL(file, maxW = 480, maxH = 200) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) return reject(new Error("Please choose an image file."));
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width, maxH / img.height);
      const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const g = c.getContext("2d");
      g.fillStyle = "#ffffff"; g.fillRect(0, 0, w, h);
      g.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      // flat logos stay crisp as PNG; photos go JPEG
      resolve(/png|svg|webp|gif/.test(file.type) ? c.toDataURL("image/png") : c.toDataURL("image/jpeg", 0.88));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("That image couldn't be read.")); };
    img.src = url;
  });
}

// Logos saved before this fix may still be transparent. Flatten them onto white when a PDF is built.
export function flattenLogoOnWhite(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl || typeof document === "undefined" || typeof Image === "undefined") return resolve(dataUrl);
    if (/^data:image\/jpe?g/i.test(dataUrl)) return resolve(dataUrl); // JPEG can't be transparent
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.width; c.height = img.height;
        const g = c.getContext("2d");
        g.fillStyle = "#ffffff"; g.fillRect(0, 0, c.width, c.height);
        g.drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch (e) { resolve(dataUrl); }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ---- invoice review & email -------------------------------------------------

const MON3 = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const prettyDate = (iso) => (iso && iso.length >= 10 ? `${MON3[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)}, ${iso.slice(0, 4)}` : "");

// Things worth a second look before an invoice goes to a client.
// level "error" blocks nothing by itself, but is shown prominently.
export function invoiceChecks(inv, client, profile) {
  const biz = profile.business || {};
  const t = invoiceTotals(inv);
  const out = [];
  if (!(inv.items || []).length || t.total <= 0) out.push({ level: "error", text: "The total is zero. Add at least one priced line." });
  if ((inv.items || []).some((it) => !String(it.description || "").trim() && !String(it.activity || "").trim())) out.push({ level: "warn", text: "A line has no description." });
  if (!client) out.push({ level: "warn", text: "No client selected, so it will say “Billed to —”." });
  if (!biz.name && !biz.logo) out.push({ level: "warn", text: "No business name or logo yet.", action: "details" });
  if (!biz.paymentInfo) out.push({ level: "warn", text: "No payment instructions, so clients won't know how to pay.", action: "details" });
  if (inv.dueDate && inv.issueDate && inv.dueDate < inv.issueDate) out.push({ level: "warn", text: "The due date is before the issue date." });
  if ((profile.invoices || []).some((i) => i.id !== inv.id && String(i.number).trim() === String(inv.number).trim())) out.push({ level: "warn", text: `Another invoice already uses the number ${inv.number}.` });
  return out;
}

// A pre-filled message for the person's own mail app. mailto: links cannot
// attach files, so the PDF has to be attached by hand — the review screen says so.
export function buildInvoiceEmail(inv, client, profile, fmt, link) {
  const biz = profile.business || {};
  const name = biz.name || profile.name;
  const t = invoiceTotals(inv);
  const lines = [`Hi ${client?.name || "there"},`, "", `Please find invoice ${inv.number} for ${fmt(t.total)}, due ${prettyDate(inv.dueDate)}.`, ""];
  (inv.items || []).slice(0, 8).forEach((it) => lines.push(`• ${[it.activity, it.description].filter(Boolean).join(" — ")}: ${fmt((Number(it.qty) || 0) * (Number(it.rate) || 0))}`));
  if ((inv.items || []).length > 8) lines.push(`• …and ${(inv.items || []).length - 8} more`);
  lines.push("");
  if (link) lines.push(`View${inv.status === "paid" ? "" : " and pay"} online: ${link}`, "");
  if (biz.paymentInfo) lines.push("How to pay:", biz.paymentInfo, "");
  if (inv.notes) lines.push(inv.notes, "");
  lines.push("Thank you,", name);
  return { subject: `Invoice ${inv.number} from ${name}`, body: lines.join("\n") };
}

// Receipts reuse the invoice's number so the two are easy to match up: INV-0007 → RCT-0007.
export function receiptNumber(inv, payment) {
  const n = String(inv.number || "");
  const base = /^inv/i.test(n) ? n.replace(/^inv/i, "RCT") : `RCT-${n}`;
  if (!payment) return base;
  const idx = (inv.payments || []).findIndex((p) => p.id === payment.id);
  return `${base}-${idx + 1}`;
}

export function buildReceiptEmail(inv, client, profile, fmt, payment) {
  const biz = profile.business || {};
  const name = biz.name || profile.name;
  const t = invoiceTotals(inv);
  const lines = [`Hi ${client?.name || "there"},`, ""];
  if (payment) {
    const idx = (inv.payments || []).findIndex((p) => p.id === payment.id);
    const after = cents(t.total - (inv.payments || []).slice(0, idx + 1).reduce((x, p) => x + (Number(p.amount) || 0), 0));
    lines.push(`Thank you! We've received your payment of ${fmt(payment.amount)} for invoice ${inv.number} on ${prettyDate(payment.date)}.`);
    lines.push(after > 0 ? `The remaining balance on this invoice is ${fmt(after)}.` : "This invoice is now paid in full.");
    lines.push("", `Your receipt (${receiptNumber(inv, payment)}) is attached.`);
  } else {
    lines.push(`Thank you! We've received your payment of ${fmt(t.total)} for invoice ${inv.number}${inv.paidDate ? ` on ${prettyDate(inv.paidDate)}` : ""}.`, "", `Your receipt (${receiptNumber(inv)}) is attached.`);
  }
  lines.push("", "Thank you,", name);
  return { subject: `Receipt ${receiptNumber(inv, payment)} from ${name}`, body: lines.join("\n") };
}

export function mailtoLink(to, subject, body) {
  // keep well under the ~2,000-character limit some mail clients enforce
  let b = body;
  const build = () => `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(b)}`;
  let url = build();
  while (url.length > 1900 && b.length > 200) { b = b.slice(0, Math.floor(b.length * 0.85)).trimEnd() + "…"; url = build(); }
  return url;
}

// ---- overdue & reminders ------------------------------------------------------

// "overdue" is worked out from the due date, not typed by hand: a sent invoice past its due date.
export function effectiveStatus(inv, today = localToday()) {
  if (inv.status === "sent" && inv.dueDate && inv.dueDate < today) return "overdue";
  return inv.status;
}
export function daysOverdue(inv, today = localToday()) {
  if (!inv.dueDate || inv.dueDate >= today) return 0;
  return Math.round((parse(today) - parse(inv.dueDate)) / 86400000);
}
export function outstandingSummary(invoices, today = localToday()) {
  let outstanding = 0, overdue = 0, overdueCount = 0, openCount = 0;
  (invoices || []).forEach((i) => {
    const s = effectiveStatus(i, today);
    if (s === "paid" || s === "draft") return;
    const t = invoiceBalance(i);
    outstanding += t; openCount++;
    if (s === "overdue") { overdue += t; overdueCount++; }
  });
  return { outstanding, overdue, overdueCount, openCount };
}
// Tone escalates with how late it is.
export function reminderTone(inv, today = localToday()) {
  const d = daysOverdue(inv, today);
  if (d <= 0) return "upcoming";
  if (d <= 7) return "friendly";
  if (d <= 21) return "firm";
  return "final";
}
export function buildReminderEmail(inv, client, profile, fmt, link, today = localToday()) {
  const biz = profile.business || {};
  const name = biz.name || profile.name;
  const t = invoiceTotals(inv);
  const tone = reminderTone(inv, today);
  const d = daysOverdue(inv, today);
  const hi = `Hi ${client?.name || "there"},`;
  const bal = invoiceBalance(inv);
  const facts = bal < t.total
    ? `Invoice ${inv.number} has ${fmt(bal)} still outstanding (of ${fmt(t.total)}) and was due on ${prettyDate(inv.dueDate)}`
    : `Invoice ${inv.number} for ${fmt(t.total)} was due on ${prettyDate(inv.dueDate)}`;
  const lines = [hi, ""];
  if (tone === "upcoming") lines.push(`A quick reminder that ${facts.replace("was due", "is due")}.`);
  else if (tone === "friendly") lines.push(`${facts}, so it's now ${d} day${d === 1 ? "" : "s"} past due. If it has already been paid, thank you and please ignore this note.`);
  else if (tone === "firm") lines.push(`${facts} and is now ${d} days overdue. Please arrange payment this week, or let me know if there's a problem with the invoice.`);
  else lines.push(`${facts} and is now ${d} days overdue. This is a final reminder before we need to discuss next steps. Please pay today or contact me to agree a plan.`);
  if (link) lines.push("", `View and pay online: ${link}`);
  if (biz.paymentInfo) lines.push("", "How to pay:", biz.paymentInfo);
  lines.push("", "Thank you,", name);
  const subj = { upcoming: "Reminder", friendly: "Friendly reminder", firm: "Overdue", final: "Final reminder" }[tone];
  return { subject: `${subj}: invoice ${inv.number} from ${name}`, body: lines.join("\n"), tone };
}
